import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Space,
  message,
  Form,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import ChargingHttpHandler from '../../shared/chargingHttpHandler';
import secureStorage from '../../shared/secureStorage';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import isBetween from 'dayjs/plugin/isBetween';

// 設置 dayjs 原本的會衝突 改用這個～～
dayjs.locale('zh-tw');
dayjs.extend(isBetween);

const { RangePicker } = DatePicker;

const OrderQuantityStats = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [memberInfo, setMemberInfo] = useState({});
  const [filterOptions, setFilterOptions] = useState({
    businessNames: [],
    statuses: [],
  });

  // 創建固定的初始日期範圍，只在組件初始化時計算一次
  const defaultDateRange = useMemo(
    () => [dayjs().subtract(30, 'days'), dayjs()],
    []
  );

  const [filters, setFilters] = useState({
    dateRange: defaultDateRange,
    businessNames: [],
    statuses: [],
    crMember: 'all', // 'all', 'cr', 'non-cr'
  });
  const [form] = Form.useForm();

  // 獲取會員資料
  const fetchMemberInfo = async () => {
    try {
      const httpHandler = new ChargingHttpHandler(false);
      const chargingAccessToken = secureStorage.getItem('chargingAccessToken');

      const response = await httpHandler
        .axios()
        .get('/platform/get_member_info', {
          headers: { Authorization: `Bearer ${chargingAccessToken}` },
        });

      const memberMap = {};
      if (response.data && Array.isArray(response.data.data)) {
        response.data.data.forEach((member) => {
          if (member.account) {
            memberMap[member.account] = {
              name: member.name || '未知',
              phone: member.phone || '未知',
              crCheck: member.cr_check || 0,
            };
          }
        });
      }
      setMemberInfo(memberMap);
    } catch (error) {
      console.error('獲取會員資料失敗', error);
    }
  };

  const add_search_log = async (success) => {
    const userRole = secureStorage.getItem('userRole');
    const httpHandler = new ChargingHttpHandler(false);
    const chargingAccessToken = secureStorage.getItem('chargingAccessToken');
    // 紀錄搜尋時間
    await httpHandler
      .axios()
      .post(
        '/platform/update_user_log_table',
        {
          log_time: dayjs().format('YYYY/MM/DD HH:mm:ss'),
          user_role: userRole,
          action_type: '訂單數量搜尋',
          action_content: '用戶搜尋訂單數量資訊',
          status: success,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${chargingAccessToken}`,
          },
        }
      )
      .catch((e) => {
        console.error('寫入搜尋紀錄失敗', e);
      });
  };

  // 獲取訂單資料
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const httpHandler = new ChargingHttpHandler(false);
      const chargingAccessToken = secureStorage.getItem('chargingAccessToken');

      const response = await httpHandler.axios().get('/order/all-cdr-info', {
        headers: { Authorization: `Bearer ${chargingAccessToken}` },
      });

      if (response.data && Array.isArray(response.data.data)) {
        await add_search_log(1);
        setOrders(response.data.data);
        generateFilterOptions(response.data.data);
      } else {
        await add_search_log(0);
      }
    } catch (error) {
      console.error('獲取訂單資料失敗', error);
      message.error('獲取訂單資料失敗');
    } finally {
      setLoading(false);
    }
  };

  const generateFilterOptions = (orders) => {
    if (!orders || orders.length === 0) return;

    // 營運商名稱選項
    const businessNames = [
      ...new Set(orders.map((order) => order.businessName).filter(Boolean)),
    ].map((businessName) => ({ label: businessName, value: businessName }));

    // 狀態選項
    const statuses = [
      ...new Set(orders.map((order) => order.status).filter(Boolean)),
    ].map((status) => ({ label: status, value: status }));

    setFilterOptions({
      businessNames,
      statuses,
    });
  };

  // 處理搜尋
  const handleSearch = () => {
    form.submit();
  };

  // 處理表單提交
  const handleFormSubmit = (values) => {
    setFilters({
      dateRange: values.dateRange,
      businessNames: values.businessNames || [],
      statuses: values.statuses || [],
      crMember: values.crMember || 'all',
    });
    message.success('搜尋完成');
  };

  // 重置日期範圍
  const resetDateRange = () => {
    form.setFieldsValue({
      dateRange: defaultDateRange,
      businessNames: [],
      statuses: [],
      crMember: 'all',
    });
    setFilters({
      dateRange: defaultDateRange,
      businessNames: [],
      statuses: [],
      crMember: 'all',
    });
  };

  // 狀態顏色映射（只保留5種狀態）
  const statusColorMap = {
    無需付款: '#73d13d',
    付款成功: '#1890ff',
    結算中: '#faad14',
    付款失敗: '#ff4d4f',
    訂單作廢: '#d9d9d9',
  };

  // 圖表資料
  const generateChartData = () => {
    if (!orders.length) return [];

    const { dateRange, businessNames, statuses, crMember } = filters;

    // 篩選資料
    let filteredOrders = orders.filter((order) => {
      let inDateRange = true;
      if (dateRange && dateRange.length === 2) {
        const [startDate, endDate] = dateRange;
        const orderDate = dayjs(order.createdTime);
        inDateRange = orderDate.isBetween(startDate, endDate, 'day', '[]');
      }

      const businessMatch =
        businessNames.length === 0 ||
        businessNames.some((business) => order.businessName === business);

      const statusMatch =
        statuses.length === 0 ||
        statuses.some((status) => order.status === status);

      // CR會員篩選
      let crMatch = true;
      if (crMember !== 'all') {
        const member = memberInfo[order.userId];
        if (member) {
          const isCRMember = member.crCheck === 1;
          crMatch = crMember === 'cr' ? isCRMember : !isCRMember;
        } else {
          // 如果找不到會員資料，視為非CR會員
          crMatch = crMember === 'non-cr';
        }
      }

      return inDateRange && businessMatch && statusMatch && crMatch;
    });

    // 按日期分組統計
    const dateGroups = {};

    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      let currentDate = startDate.clone();
      while (currentDate.isSameOrBefore(endDate, 'day')) {
        const dateStr = currentDate.format('MM/DD');
        dateGroups[dateStr] = { date: dateStr };
        currentDate = currentDate.clone().add(1, 'day'); // 創建新的克隆對象
      }
    }

    // 統計各狀態訂單數量
    filteredOrders.forEach((order) => {
      const dateStr = dayjs(order.createdTime).format('MM/DD');
      if (dateGroups[dateStr]) {
        const status = order.status;
        if (!dateGroups[dateStr][status]) {
          dateGroups[dateStr][status] = 0;
        }
        dateGroups[dateStr][status]++;
      }
    });

    // 轉換為 recharts 所需格式（每個日期一個對象，狀態作為鍵）
    return Object.values(dateGroups);
  };

  // 獲取所有可能的狀態列表
  const getAllStatuses = () => {
    return Object.keys(statusColorMap);
  };

  // 圖表資料
  const chartData = generateChartData();

  useEffect(() => {
    fetchMemberInfo();
    fetchOrders();
  }, []);

  return (
    <div>
      {/* 篩選區域 */}
      <Card style={{ marginBottom: 16 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          initialValues={{
            dateRange: defaultDateRange,
            businessNames: [],
            statuses: [],
            crMember: 'all',
          }}>
          <Row gutter={[16, 16]} align="middle">
            <Col span={6}>
              <Form.Item label="日期範圍" name="dateRange">
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="營運商 (可複選)" name="businessNames">
                <Select
                  mode="multiple"
                  placeholder="請選擇營運商"
                  style={{ width: '100%' }}
                  options={filterOptions.businessNames}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="訂單狀態 (可複選)" name="statuses">
                <Select
                  mode="multiple"
                  placeholder="請選擇訂單狀態"
                  style={{ width: '100%' }}
                  options={filterOptions.statuses}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="CR會員篩選" name="crMember">
                <Select
                  placeholder="請選擇會員類型"
                  style={{ width: '100%' }}
                  options={[
                    { label: '全部', value: 'all' },
                    { label: 'CR會員', value: 'cr' },
                    { label: '非CR會員', value: 'non-cr' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]} align="middle">
            <Col span={24} style={{ textAlign: 'right' }}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={loading}>
                  搜尋
                </Button>
                <Button onClick={resetDateRange}>重置篩選</Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* 圖表區域 */}
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>訂單數量統計報表</h3>
        </div>

        {/* 圖例說明 */}
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
          }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: '#73d13d',
                marginRight: 8,
              }}></div>
            <span>無需付款</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: '#1890ff',
                marginRight: 8,
              }}></div>
            <span>付款成功</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: '#faad14',
                marginRight: 8,
              }}></div>
            <span>結算中</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: '#ff4d4f',
                marginRight: 8,
              }}></div>
            <span>付款失敗</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                width: 16,
                height: 16,
                backgroundColor: '#d9d9d9',
                marginRight: 8,
              }}></div>
            <span>訂單作廢</span>
          </div>
        </div>

        {/* 長條圖 */}
        <div style={{ height: 500, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Legend />
              {getAllStatuses().map((status) => (
                <Bar
                  key={status}
                  dataKey={status}
                  stackId="a"
                  fill={statusColorMap[status]}
                  name={status}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};

export default OrderQuantityStats;
